import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Grid,
  MenuItem,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Sync as SyncIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import useAlert from "../../../../hooks/useAlert";
import { validatePlaceProductTrigger } from "../../../../utils/validation";
import {
  AddPlaceProductTrigger,
  UpdatePlaceProductTrigger,
  GetPlaceProductTrigger,
} from "../../../../redux/actions/productSyncActions";
import SectionCard from "../SectionCard";

const TriggerBaseForm = ({ placeOptions, trigger, onSaved }) => {
  const authUserId = useSelector((state) => state?.auth?.user?.id);
  const dispatch = useDispatch();
  const { showSuccessAlertAllNoReload, showErrorAlertAll } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [baseKey, setBaseKey] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const dataExists = !!trigger;

  useEffect(() => {
    if (trigger) {
      if (trigger.baseMarketPlaceId) setBaseKey(`mp-${trigger.baseMarketPlaceId}`);
      else if (trigger.baseEcommercePlaceId) setBaseKey(`ec-${trigger.baseEcommercePlaceId}`);
    }
  }, [trigger]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validatePlaceProductTrigger({ baseKey });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const [sourceType, rawId] = baseKey.split("-");
    const payload = {
      userId: authUserId,
      baseMarketPlaceId: sourceType === "mp" ? Number(rawId) : null,
      baseEcommercePlaceId: sourceType === "ec" ? Number(rawId) : null,
      nextRun: dataExists
        ? trigger.nextRun
        : new Date(Date.now() + 10 * 60 * 1000).toISOString(),
      ia: true,
    };
    if (dataExists) {
      payload.id = trigger.id;
    }

    try {
      setSubmitting(true);
      dataExists
        ? await dispatch(UpdatePlaceProductTrigger(payload))
        : await dispatch(AddPlaceProductTrigger(payload));
      // Create/Update yanıtındaki entity'ye güvenmek yerine (backend id'yi
      // eksik/0 döndürebiliyor), kaydettikten sonra sunucudan taze GET ile
      // gerçek id'yi doğrulayıp state'e onu yazıyoruz.
      const freshList = await dispatch(GetPlaceProductTrigger(authUserId));
      showSuccessAlertAllNoReload();
      onSaved(freshList && freshList.length > 0 ? freshList[0] : payload);
    } catch {
      showErrorAlertAll();
    } finally {
      setSubmitting(false);
    }
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.5,
    },
  };

  return (
    <SectionCard
      icon={SyncIcon}
      title="Kaynak Platform"
      description="Ürünlerin hangi pazaryeri/e-ticaret platformundan temel alınacağını seçin"
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              select
              size={isMobile ? "small" : "medium"}
              fullWidth
              label="Kaynak Pazaryeri / E-ticaret"
              value={baseKey}
              onChange={(e) => setBaseKey(e.target.value)}
              error={!!formErrors.baseKey}
              helperText={formErrors.baseKey}
              sx={textFieldSx}
            >
              {placeOptions.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>
                  {opt.name} ({opt.sourceType === "marketplace" ? "Pazaryeri" : "E-ticaret"})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                borderRadius: 1.5,
                backgroundColor: "#1F1B45",
                color: "#FFFFFF",
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.813rem", sm: "0.875rem" },
                padding: { xs: "8px 20px", sm: "10px 24px" },
                minWidth: { xs: "120px", sm: "140px" },
                boxShadow: "none",
                "&:hover": { backgroundColor: "#2C285A", boxShadow: "none" },
              }}
            >
              {submitting ? (
                <CircularProgress size={isMobile ? 20 : 24} sx={{ color: "inherit" }} />
              ) : dataExists ? (
                "Güncelle"
              ) : (
                "Oluştur"
              )}
            </Button>
          </Grid>
          {dataExists && trigger?.nextRun && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Bir sonraki çalışma zamanı: {new Date(trigger.nextRun).toLocaleString("tr-TR")}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </SectionCard>
  );
};

export default TriggerBaseForm;
